import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Statistic,
  Empty,
  Spin,
  Alert,
  Table,
  Tag,
} from 'antd';
import {
  FileTextOutlined,
  SearchOutlined,
  MailOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { subscriptionApi } from '../../services/subscription';
import { COLORS } from '../../constants/colors';
import type { UsageSummary, Subscription } from '../../types/subscription';

const { Title, Text } = Typography;

// 指标图标映射
const metricIcons: Record<string, React.ReactNode> = {
  requirement_analysis: <FileTextOutlined />,
  product_insight: <BarChartOutlined />,
  pricing: <FileTextOutlined />,
  reference_influencer: <SearchOutlined />,
  proposal_excel: <FileTextOutlined />,
  proposal_ppt: <ThunderboltOutlined />,
  influencer_search: <SearchOutlined />,
  influencer_evaluate: <BarChartOutlined />,
  influencer_insight: <BarChartOutlined />,
  outreach_generate: <MailOutlined />,
  email_send: <MailOutlined />,
};

export const Usage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [usageSummary, setUsageSummary] = useState<UsageSummary[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usageRes, subRes] = await Promise.all([
        subscriptionApi.getUsageSummary(),
        subscriptionApi.getCurrentSubscription().catch(() => null),
      ]);

      if (usageRes.data) {
        setUsageSummary(usageRes.data);
      }
      if (subRes?.data) {
        setSubscription(subRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 计算统计数据
  const getStats = () => {
    const totalUsed = usageSummary.reduce((sum, item) => sum + item.used, 0);
    const warningCount = usageSummary.filter(item => !item.is_unlimited && item.percentage >= 80 && item.percentage < 100).length;
    const exceededCount = usageSummary.filter(item => !item.is_unlimited && item.percentage >= 100).length;
    
    return { totalUsed, warningCount, exceededCount };
  };

  const stats = getStats();

  // 获取进度条颜色
  const getProgressColor = (percentage: number, isUnlimited: boolean) => {
    if (isUnlimited) return COLORS.success;
    if (percentage >= 100) return COLORS.error;
    if (percentage >= 80) return COLORS.warning;
    return COLORS.primary;
  };

  // 获取状态标签
  const getStatusTag = (percentage: number, isUnlimited: boolean) => {
    if (isUnlimited) return <Tag color="green">无限</Tag>;
    if (percentage >= 100) return <Tag color="red">已超限</Tag>;
    if (percentage >= 80) return <Tag color="orange">即将超限</Tag>;
    return <Tag color="blue">正常</Tag>;
  };

  // 表格列定义
  const columns = [
    {
      title: '指标',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text: string, record: UsageSummary) => (
        <Space>
          {metricIcons[record.metric_type] || <BarChartOutlined />}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '已使用',
      dataIndex: 'used',
      key: 'used',
      render: (used: number) => <Text strong>{used}</Text>,
    },
    {
      title: '配额',
      dataIndex: 'limit',
      key: 'limit',
      render: (limit: number, record: UsageSummary) => (
        record.is_unlimited ? <Text type="secondary">无限</Text> : <Text>{limit}</Text>
      ),
    },
    {
      title: '使用率',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number, record: UsageSummary) => (
        record.is_unlimited ? (
          <Progress percent={100} size="small" showInfo={false} strokeColor={COLORS.success} />
        ) : (
          <Progress 
            percent={Math.min(percentage, 100)} 
            size="small" 
            strokeColor={getProgressColor(percentage, false)}
          />
        )
      ),
    },
    {
      title: '状态',
      key: 'status',
      render: (_: unknown, record: UsageSummary) => getStatusTag(record.percentage, record.is_unlimited),
    },
  ];

  // 导入 Space 组件
  const Space = ({ children, ...props }: { children: React.ReactNode; [key: string]: unknown }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} {...props}>{children}</div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        用量统计
      </Title>

      {/* 警告提示 */}
      {stats.exceededCount > 0 && (
        <Alert
          message={`您有 ${stats.exceededCount} 个指标已超限，请尽快升级订阅`}
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}
      {stats.warningCount > 0 && stats.exceededCount === 0 && (
        <Alert
          message={`您有 ${stats.warningCount} 个指标即将超限（使用率超过80%）`}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="当前版本"
              value={subscription?.plan?.name || '-'}
              prefix={<CheckCircleOutlined style={{ color: COLORS.success }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总使用次数"
              value={stats.totalUsed}
              prefix={<BarChartOutlined style={{ color: COLORS.primary }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="即将超限"
              value={stats.warningCount}
              valueStyle={{ color: stats.warningCount > 0 ? COLORS.warning : undefined }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已超限"
              value={stats.exceededCount}
              valueStyle={{ color: stats.exceededCount > 0 ? COLORS.error : undefined }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 详细用量 */}
      <Card title="详细用量">
        {usageSummary.length === 0 ? (
          <Empty description="暂无用量数据" />
        ) : (
          <Row gutter={[16, 16]}>
            {usageSummary.map((item) => (
              <Col span={8} key={item.metric_type}>
                <Card 
                  size="small" 
                  style={{ 
                    borderColor: item.is_unlimited ? undefined : (
                      item.percentage >= 100 ? COLORS.error : 
                      item.percentage >= 80 ? COLORS.warning : undefined
                    )
                  }}
                >
                  <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                      {metricIcons[item.metric_type] || <BarChartOutlined />}
                      <Text strong>{item.display_name}</Text>
                    </Space>
                    {getStatusTag(item.percentage, item.is_unlimited)}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">已使用：</Text>
                    <Text strong style={{ fontSize: 18 }}>{item.used}</Text>
                    {!item.is_unlimited && (
                      <Text type="secondary"> / {item.limit}</Text>
                    )}
                  </div>
                  <Progress
                    percent={item.is_unlimited ? 100 : Math.min(item.percentage, 100)}
                    strokeColor={getProgressColor(item.percentage, item.is_unlimited)}
                    showInfo={!item.is_unlimited}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
};
